import React, { useCallback, useMemo } from "react";
import { Entity } from "@common/types/entity";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { MonetaryInput } from "@design-system/MonetaryInput/MonetaryInput";
import { TextArea } from "@design-system/TextArea/TextArea";
import { ColumnDef } from "@components/spreadsheet/types";
import { CounterpartyCombobox } from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { FromEntitySelect } from "@components/invoices/NativeInvoiceDrawer/FromEntitySelect";
import { toContactOption } from "@components/invoices/NativeInvoiceDrawer/contactOption";
import drawerStyles from "../AccrualImporterDrawer.module.scss";
import {
    formatAccrualImporterDate,
    parseAccrualImporterDateToIso,
} from "../accrualImporterDateFormat";
import { AccrualImporterRow, AccrualMode } from "../types";
import { useAccrualImporterHeaderFieldUpdate } from "./useAccrualImporterHeaderFieldUpdate";
import styles from "./AccrualImporterGroupedSections.module.scss";

interface Props {
    mode: AccrualMode;
    row: AccrualImporterRow;
    columns: ColumnDef[];
    setRows: React.Dispatch<React.SetStateAction<AccrualImporterRow[]>>;
    globalRowIndex: number;
    canRemoveGroup?: boolean;
    onRemoveGroup?: () => void;
    removeGroupLabel?: string;
}

function getColumnLabel(
    columns: ColumnDef[],
    field: string,
    fallback: string,
): string {
    const column = columns.find((entry) => entry.field === field);
    if (!column || typeof column.label !== "string") {
        return fallback;
    }
    return column.label;
}

function getEntitiesFromColumns(columns: ColumnDef[]): Entity[] {
    const entityColumn = columns.find((column) => column.field === "entityId");
    if (!entityColumn || !Array.isArray(entityColumn.entities)) {
        return [];
    }
    return entityColumn.entities;
}

function getCounterpartiesMapFromColumns(
    columns: ColumnDef[],
): Map<string, string> {
    const counterpartyColumn = columns.find(
        (column) => column.field === "counterpartyId",
    );
    if (
        !counterpartyColumn ||
        !(counterpartyColumn.counterpartiesMap instanceof Map)
    ) {
        return new Map();
    }
    return counterpartyColumn.counterpartiesMap;
}

export const AccrualImporterHeaderDetails: React.FC<Props> = ({
    mode,
    row,
    columns,
    setRows,
    globalRowIndex,
    canRemoveGroup = false,
    onRemoveGroup,
    removeGroupLabel,
}) => {
    const isBill = mode === "bill";
    const entities = useMemo(() => getEntitiesFromColumns(columns), [columns]);
    const counterpartiesMap = useMemo(
        () => getCounterpartiesMapFromColumns(columns),
        [columns],
    );
    const { updateField, updateCounterparty } =
        useAccrualImporterHeaderFieldUpdate({
            setRows,
            globalRowIndex,
        });

    const selectedEntity = useMemo(
        () => entities.find((entity) => entity.id === row.entityId),
        [entities, row.entityId],
    );

    const selectedCounterparty = useMemo(() => {
        if (!row.counterpartyId) {
            return undefined;
        }

        const resolvedName =
            counterpartiesMap.get(row.counterpartyId) ??
            row.counterpartyName ??
            undefined;

        return {
            id: row.counterpartyId,
            name: resolvedName ?? "Unnamed contact",
        };
    }, [counterpartiesMap, row.counterpartyId, row.counterpartyName]);

    // Scoped to invoices only: the "from entity" on an existing invoice is
    // immutable (drives accounting/JE posting), but bills don't have that
    // requirement here, so this guard leaves bill behavior unchanged.
    const isExistingInvoiceEntityLocked = !isBill && Boolean(row.accrualId);

    const handleSelectEntity = useCallback(
        (entity: Entity | undefined) => {
            // Defense in depth: the picker is disabled for existing invoice
            // rows, but guard the handler too so a programmatic call can't
            // slip through.
            if (isExistingInvoiceEntityLocked) {
                return;
            }
            updateField("entityId", entity?.id ?? null);
        },
        [updateField, isExistingInvoiceEntityLocked],
    );

    const handleCounterpartyChange = useCallback(
        (counterparty: { id: string; name?: string } | undefined) => {
            updateCounterparty(counterparty);
        },
        [updateCounterparty],
    );

    const totalAmountLabel = isBill ? "Bill total" : "Invoice total";
    const sectionTitle = isBill ? "Bill" : "Invoice";

    return (
        <>
            <div className={styles.headerDetailsToolbar}>
                <span className={styles.headerDetailsTitle}>
                    {sectionTitle}
                </span>
                {canRemoveGroup && onRemoveGroup && removeGroupLabel ? (
                    <button
                        type="button"
                        className={drawerStyles.addLineButton}
                        onClick={onRemoveGroup}
                    >
                        {removeGroupLabel}
                    </button>
                ) : null}
            </div>
            <div className={styles.headerDetailsFields}>
                <div className={styles.fieldGridTwo}>
                    <FromEntitySelect
                        label={getColumnLabel(columns, "entityId", "Entity")}
                        entities={entities}
                        selectedEntity={selectedEntity}
                        onSelectEntity={handleSelectEntity}
                        // Existing invoices carry an `accrualId`; the from
                        // entity is immutable once the invoice exists (it
                        // drives accounting/JE posting), so only rows still
                        // being created (no accrualId yet) allow picking it.
                        isEntityLocked={isExistingInvoiceEntityLocked}
                        entityLockedTooltip="The entity can't be changed after the invoice is created"
                    />
                    <Field
                        label={getColumnLabel(
                            columns,
                            "counterpartyId",
                            isBill ? "Vendor" : "Customer",
                        )}
                        control="button"
                        className={styles.buttonField}
                    >
                        <CounterpartyCombobox
                            selectedCounterparty={selectedCounterparty}
                            onChange={handleCounterpartyChange}
                            placeholder={
                                isBill ? "Select vendor" : "Select customer"
                            }
                            toCounterpartyOption={toContactOption}
                            triggerButtonProps={{
                                variant: "primary",
                                size: "xl",
                            }}
                        />
                    </Field>
                </div>
                <div className={styles.fieldGridFour}>
                    <Field
                        label={getColumnLabel(
                            columns,
                            "createdDate",
                            "Created date",
                        )}
                        control="button"
                        className={styles.buttonField}
                    >
                        <Field.Control
                            render={(controlProps) => (
                                <DatePicker
                                    mode="day"
                                    variant="panel-input"
                                    clearable={false}
                                    value={
                                        parseAccrualImporterDateToIso(
                                            row.createdDate,
                                        ) ?? undefined
                                    }
                                    onValueChange={(value) =>
                                        updateField(
                                            "createdDate",
                                            value
                                                ? formatAccrualImporterDate(
                                                      value,
                                                  )
                                                : "",
                                        )
                                    }
                                    placeholder="Select date"
                                    buttonProps={{
                                        variant: "primary",
                                        size: "sm",
                                    }}
                                    fieldControlProps={pickFieldControlAriaProps(
                                        controlProps,
                                    )}
                                />
                            )}
                        />
                    </Field>
                    <Field
                        label={getColumnLabel(columns, "dueDate", "Due date")}
                        control="button"
                        className={styles.buttonField}
                    >
                        <Field.Control
                            render={(controlProps) => (
                                <DatePicker
                                    mode="day"
                                    variant="panel-input"
                                    clearable
                                    value={
                                        parseAccrualImporterDateToIso(
                                            row.dueDate,
                                        ) ?? undefined
                                    }
                                    onValueChange={(value) =>
                                        updateField(
                                            "dueDate",
                                            value
                                                ? formatAccrualImporterDate(
                                                      value,
                                                  )
                                                : "",
                                        )
                                    }
                                    placeholder="Select date"
                                    buttonProps={{
                                        variant: "primary",
                                        size: "sm",
                                    }}
                                    fieldControlProps={pickFieldControlAriaProps(
                                        controlProps,
                                    )}
                                />
                            )}
                        />
                    </Field>
                    <Field
                        label={getColumnLabel(
                            columns,
                            "invoiceNumber",
                            isBill ? "Bill number" : "Invoice number",
                        )}
                        className={styles.textField}
                    >
                        <Input
                            size="xl"
                            maxLength={255}
                            value={row.invoiceNumber}
                            onChange={(event) =>
                                updateField("invoiceNumber", event.target.value)
                            }
                        />
                    </Field>
                    <Field
                        label={totalAmountLabel}
                        className={styles.textField}
                    >
                        <MonetaryInput
                            value={row.totalAmount ?? undefined}
                            onValueChange={(value) =>
                                updateField("totalAmount", value ?? null)
                            }
                            size="sm"
                            showCurrencySymbolIfEmpty
                            allowNegative
                            selectOnFocus
                        />
                    </Field>
                </div>
                <Field
                    label={getColumnLabel(columns, "memo", "Memo")}
                    className={styles.textField}
                >
                    <TextArea
                        size="xl"
                        rows={2}
                        resizable
                        maxLength={2000}
                        value={row.memo}
                        onChange={(event) =>
                            updateField("memo", event.target.value)
                        }
                    />
                </Field>
            </div>
        </>
    );
};
